import { Injectable } from '@angular/core';
import type { VehicleActiveMembership } from '../../../shared/utils/active-membership-plate-warning.util';
import { Order } from '../../../orders/models/order.models';
import {
  PaymentTransactionHistory,
  CardDetails,
  BillingDetails,
} from '../../../services/payment.service';
import { FamilyPlanVehicle } from '../../../shared/types';
import { SuccessModalData } from '../../../shared/components/success-modal/success-modal.component';
import { PAYMENT_SUCCESS_MODAL_DEFAULTS } from '../../../shared/utils/payment-success-modal.util';
import {
  BuiltInPaymentMethodCode,
  PaymentMethodCode,
} from '../../../shared/types/payment-method.enum';

export type BuiltInPaymentMethod = BuiltInPaymentMethodCode;
export type PaymentMethod = BuiltInPaymentMethodCode | string;

export interface PaymentForm {
  method: PaymentMethod;
  phoneNumber: string;
  cardholderName: string;
  billingAddress: string;
}

const DEFAULT_SUCCESS_MODAL: SuccessModalData = { ...PAYMENT_SUCCESS_MODAL_DEFAULTS };

/**
 * Component-scoped service that holds all shared state for the order-details-modal.
 * Provided in `OrderDetailsModalComponent.providers` so all tab components share the same instance.
 */
@Injectable()
export class OrderDetailsStateService {
  // ── Core ──────────────────────────────────────────────────────────────────
  order: Order | null = null;
  apiOrderData: any = null;

  // ── Payment transaction history ───────────────────────────────────────────
  transactionHistories: PaymentTransactionHistory[] = [];
  originalOrderTransactionHistories: PaymentTransactionHistory[] = [];
  originalOrderId: string | null = null;
  originalOrderNumber: string | null = null;
  originalOrderCardDetails: CardDetails | null = null;
  originalOrderBillingDetails: BillingDetails | null = null;
  cardDetails: CardDetails | null = null;
  billingDetails: BillingDetails | null = null;
  isLoadingPaymentDetails = false;
  paymentDetailsError = false;

  // ── Payment form ──────────────────────────────────────────────────────────
  paymentForm: PaymentForm = this.defaultPaymentForm();
  paymentIntentClientSecret = '';
  isProcessingPayment = false;
  isCreatingPaymentLink = false;
  isConfirmingCashPayment = false;
  showMorePaymentMethods = false;
  paymentLinkError = '';
  showPaymentLinkSentModal = false;

  // Stripe element completion flags
  stripeCardNumberComplete = false;
  stripeCardExpiryComplete = false;
  stripeCardCvcComplete = false;

  // ── Payment success modal ─────────────────────────────────────────────────
  showPaymentSuccess = false;
  successModalData: SuccessModalData = { ...DEFAULT_SUCCESS_MODAL };

  // ── Family plan vehicles ──────────────────────────────────────────────────
  familyPlanVehicles: FamilyPlanVehicle[] = [];
  selectedVehicleId: string | null = null;
  editingFamilyVehicle: FamilyPlanVehicle | null = null;

  // ── Vehicle edit form ─────────────────────────────────────────────────────
  isVehicleEditMode = false;
  vehicleFetchSuccess = false;
  /** Inline note under the plate field after a lookup miss; '' when nothing to show. */
  vehicleLookupMessage = '';
  /**
   * Set when the vehicle's current details could not be loaded on opening the edit form
   * (CWS-1384). Without it a failed load silently blanks Year/Make/Model and the
   * operator has no idea why the form is empty.
   */
  vehicleDetailsLoadError = '';
  /** Live memberships on the vehicle being edited; drives the plate-change warning. */
  vehicleActiveMemberships: VehicleActiveMembership[] = [];
  hasAttemptedFetch = false;
  isLoadingVehicleDetails = false;
  showVehicleMobileMenu = false;
  isLoadingVehicle = false;
  vehicleForm: {
    state: string;
    licensePlate: string;
    vehicle: string;
    year: string;
    make: string;
    model: string;
    fetchedVehicleId?: string;
    /** Owner of the fetched row; null/undefined for a row nobody owns (lookup- or camera-created). */
    fetchedVehicleCustomerId?: string | null;
  } = this.defaultVehicleForm();

  // ── Cancel order flow ─────────────────────────────────────────────────────
  showCancelOrderModal = false;
  showCancellationSuccessModal = false;
  cancellationRefundAmount = '';
  cancellationWasScheduled = false;

  // ── Timing (used by logger) ───────────────────────────────────────────────
  modalOpenTime = 0;
  paymentStartTime = 0;

  /**
   * Resets the order details modal state to its default values.
   * @example
   * reset()
   * void
   * @returns {void} Does not return a value.
   **/
  reset(): void {
    this.apiOrderData = null;
    this.transactionHistories = [];
    this.originalOrderTransactionHistories = [];
    this.originalOrderId = null;
    this.originalOrderNumber = null;
    this.originalOrderCardDetails = null;
    this.originalOrderBillingDetails = null;
    this.cardDetails = null;
    this.billingDetails = null;
    this.isLoadingPaymentDetails = false;
    this.paymentDetailsError = false;
    this.paymentForm = this.defaultPaymentForm();
    this.paymentIntentClientSecret = '';
    this.isProcessingPayment = false;
    this.isCreatingPaymentLink = false;
    this.isConfirmingCashPayment = false;
    this.showMorePaymentMethods = false;
    this.paymentLinkError = '';
    this.showPaymentLinkSentModal = false;
    this.stripeCardNumberComplete = false;
    this.stripeCardExpiryComplete = false;
    this.stripeCardCvcComplete = false;
    this.showPaymentSuccess = false;
    this.successModalData = { ...DEFAULT_SUCCESS_MODAL };
    this.familyPlanVehicles = [];
    this.selectedVehicleId = null;
    this.editingFamilyVehicle = null;
    this.isVehicleEditMode = false;
    this.vehicleFetchSuccess = false;
    this.vehicleLookupMessage = '';
    this.vehicleDetailsLoadError = '';
    this.vehicleActiveMemberships = [];
    this.hasAttemptedFetch = false;
    this.isLoadingVehicleDetails = false;
    this.showVehicleMobileMenu = false;
    this.isLoadingVehicle = false;
    this.vehicleForm = this.defaultVehicleForm();
    this.showCancelOrderModal = false;
    // showCancellationSuccessModal, cancellationRefundAmount, and cancellationWasScheduled are
    // intentionally NOT reset here. The parent closes the overlay when orderCancelled fires, which
    // would otherwise destroy the success modal before the user sees it. These are reset explicitly
    // via onCancellationSuccessClose() / onCancellationBookAgain().
  }

  private defaultPaymentForm(): PaymentForm {
    return {
      method: PaymentMethodCode.Cash,
      phoneNumber: '',
      cardholderName: '',
      billingAddress: '',
    };
  }

  private defaultVehicleForm() {
    return { state: '', licensePlate: '', vehicle: '', year: '', make: '', model: '' };
  }
}
